import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { firstValueFrom } from 'rxjs';
import kpiService from '../services/kpiService';
import { Reservations } from '../data/models/reservation.model';
import { TripLegStatus } from './models/trip-leg-status.model';

interface KpiState {
  exceptionsCount: number;
  warningsCount: number;
  openCount: number;
}

// Initial state for the KPI slice
const initialState: KpiState = {
  exceptionsCount: 0,
  warningsCount: 0,
  openCount: 0,
};

// Utility function to count statuses
const countStatuses = (reservations: Reservations[]) => {
  let exceptionsCount = 0;
  let warningsCount = 0;
  let openCount = 0;

  reservations.forEach((reservation) => {
    reservation.tripLegs.forEach((tripLeg) => {
      switch (tripLeg.status) {
        case TripLegStatus.Canceled:
        case TripLegStatus.Abandoned:
        case TripLegStatus.Void:
          exceptionsCount++;
          break;
        case TripLegStatus.OnHold:
        case TripLegStatus.NoLoad:
        case TripLegStatus.Denied:
          warningsCount++;
          break;
        case TripLegStatus.Draft:
        case TripLegStatus.Open:
        case TripLegStatus.Offered:
        case TripLegStatus.Accepted:
          openCount++;
          break;
        // no default needed as we don't count other statuses
      }
    });
  });

  return { exceptionsCount, warningsCount, openCount };
};

// Async thunk for fetching KPI counts
export const fetchKpiCounts = createAsyncThunk(
  'kpi/fetchKpiCounts',
  async (_, { rejectWithValue }) => {
    try {
      // Convert the Observable to a Promise
      const response = await firstValueFrom(kpiService.getReservations());
      // Extract the Reservations array from the response object
      const data = response.Reservations;
      // Count the statuses
      return countStatuses(data);
    } catch (error) {
      console.error('Error fetching KPI counts:', error);
      return rejectWithValue('Failed to fetch KPI data');
    }
  },
);

// KPI slice definition
const kpiSlice = createSlice({
  name: 'kpi',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(
      fetchKpiCounts.fulfilled,
      (
        state,
        action: PayloadAction<{
          exceptionsCount: number;
          warningsCount: number;
          openCount: number;
        }>,
      ) => {
        // Update the state with the counts
        state.exceptionsCount = action.payload.exceptionsCount;
        state.warningsCount = action.payload.warningsCount;
        state.openCount = action.payload.openCount;
      },
    );
    // Optionally handle pending and rejected states if needed
  },
});

export default kpiSlice.reducer;
